import React from 'react';
import { fmtPct } from '../format';
import { textWidth } from './scale';
import { useWidth } from './useWidth';

/**
 * 100% οριζόντια μπάρα (μέρος του όλου): κενό 2px στο χρώμα της επιφάνειας, στρογγυλά μόνο τα άκρα,
 * ποσοστό ΜΕΣΑ στο κομμάτι μόνο όταν χωράει (αλλιώς μένει στον πίνακα/υπόμνημα).
 */
export type Tone = 1 | 2 | 3 | 'o';

export interface StackPart {
  key: string;
  label: string;
  value: number;
  tone: Tone;
  tip?: string;
}

const GAP = 2;
const LABEL_SIZE = 11.5;

/** Πλάτος κάθε κομματιού (px) και αν χωρά η ετικέτα του. */
export function stackLayout(parts: StackPart[], width: number): Array<{ part: StackPart; share: number; width: number; showLabel: boolean }> {
  const shown = parts.filter(p => p.value > 0);
  const total = shown.reduce((s, p) => s + p.value, 0);
  if (total <= 0) return [];
  const usable = Math.max(0, width - GAP * (shown.length - 1));
  return shown.map(part => {
    const share = (part.value / total) * 100;
    const w = (usable * part.value) / total;
    return { part, share, width: w, showLabel: w >= textWidth(fmtPct(share), LABEL_SIZE, 600) + 14 };
  });
}

export const StackedBar: React.FC<{ parts: StackPart[]; thin?: boolean; ariaLabel?: string; testId?: string }> = ({ parts, thin = false, ariaLabel, testId }) => {
  const [ref, width] = useWidth();
  const layout = stackLayout(parts, width);
  const label = ariaLabel ?? layout.map(l => `${l.part.label} ${fmtPct(l.share)}`).join(', ');
  return (
    <div ref={ref} className={`xh-stack${thin ? ' thin' : ''}`} role="img" aria-label={label} data-testid={testId}>
      {layout.map(l => (
        <i
          key={l.part.key}
          className={`xh-bar bg${l.part.tone}`}
          style={{ flex: `${l.part.value} 1 0`, color: `var(--on-viz-${l.part.tone})` }}
          title={l.part.tip || `${l.part.label} · ${fmtPct(l.share)}`}
          data-testid="stack-part"
        >
          {!thin && l.showLabel ? fmtPct(l.share) : ''}
        </i>
      ))}
    </div>
  );
};

export default StackedBar;
